/**
 * Admin Hub-queue hook (T12, ADR-0003). Wraps the Orval-generated
 * `useAdminHubQueue` over `GET /api/admin/hub-queue` — the list of plugins
 * whose sync-repo Hub PR is open but not yet merged. Powers the admin
 * Hub Queue tab. Gate: `admin:view`.
 *
 * The thin wrapper exists per the project convention (every API call
 * goes through a `hooks/` wrapper, never the generated client directly).
 * The `enabled` opt lets the tab gate the network call on the permission
 * check so the underlying `/api/admin/hub-queue` request isn't fired for
 * principals who can't see the surface — the post-render `canView`
 * short-circuit alone still issues a query whose response is dropped.
 */
import {
  adminHubOrphans,
  adminHubQueue,
  adminHubQueueForceAutoComplete,
  getAdminHubOrphansQueryKey,
  getAdminHubQueueQueryKey,
} from '@/api/generated';
import type {
  ForceHubPrAutoCompleteResponse,
  HubOrphansResponse,
  HubQueueResponse,
} from '@cotality/marketplace-contracts/endpoints/admin';
import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

export function useAdminHubQueue(options?: {
  enabled?: boolean;
}): UseQueryResult<HubQueueResponse> {
  return useQuery<HubQueueResponse>({
    queryKey: getAdminHubQueueQueryKey(),
    queryFn: ({ signal }) => adminHubQueue(signal) as Promise<HubQueueResponse>,
    enabled: options?.enabled ?? true,
  });
}

/**
 * `GET /api/admin/hub/orphans` (T3) — the persisted Hub orphans: names listed
 * in the Hub manifest with no catalog row (e.g. a plugin skipped on a
 * secret-scan finding). Distinct from the hub-queue (pending PRs). Powers the
 * Hub Queue tab's Orphans section. Same thin-wrapper + `enabled` gate as
 * {@link useAdminHubQueue}. Gate: `admin:view`.
 */
export function useAdminHubOrphans(options?: {
  enabled?: boolean;
}): UseQueryResult<HubOrphansResponse> {
  return useQuery<HubOrphansResponse>({
    queryKey: getAdminHubOrphansQueryKey(),
    queryFn: ({ signal }) => adminHubOrphans(signal) as Promise<HubOrphansResponse>,
    enabled: options?.enabled ?? true,
  });
}

/**
 * `POST /api/admin/hub-queue/:pluginId/force-auto-complete` (Task 10,
 * ADR-0003) — the operator escape hatch that turns the manual `PATCH` used
 * to unblock four stalled Hub PRs on 2026-07-29 into a supported action.
 * Gate: `admin:sync` (enforced server-side; `HubQueueTab` also hides the
 * affordance client-side). On success invalidates the hub-queue query so
 * the row's status reflects the merge once policies pass and reconcile
 * catches up.
 */
export function useForceHubPrAutoComplete(): UseMutationResult<
  ForceHubPrAutoCompleteResponse,
  Error,
  { pluginId: string }
> {
  const qc = useQueryClient();
  return useMutation<ForceHubPrAutoCompleteResponse, Error, { pluginId: string }>({
    mutationFn: ({ pluginId }) =>
      adminHubQueueForceAutoComplete(pluginId) as Promise<ForceHubPrAutoCompleteResponse>,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: getAdminHubQueueQueryKey() });
    },
  });
}
